import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { AUTH_CONFIG } from '@auth/config.token';
import { CookiesService } from '../../services/cookies';
import { PermissionsService } from '../../services/permissions';
import { isExpired, readExpMs } from './jwt.util';
import {
  extractTokenPair,
  RefreshTokenPayload,
  RefreshTokenResponse,
  SessionRefreshError,
} from './refresh-token.model';

const REFRESH_PATH = '/api/auth/refresh-token';
const CROSS_TAB_LOCK = 'securepay-token-refresh';

type LockManagerLike = {
  request<T>(name: string, fn: () => Promise<T>): Promise<T>;
};

/**
 * Owns the one and only call to `POST {baseApi}/api/auth/refresh-token`.
 *
 * Lives in `@shared` rather than `@auth` on purpose: the interceptor and
 * SessionService both need it, and putting it in `@auth` (which imports
 * `@shared`) would turn the existing benign leaf-import into a real ESM cycle.
 *
 * The refresh request is issued through `HttpBackend`, the terminal handler,
 * so it structurally cannot re-enter the interceptor chain — no recursion is
 * possible regardless of what future interceptors do.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly config = inject(AUTH_CONFIG);
  private readonly storage = inject(CookiesService);
  private readonly permissions = inject(PermissionsService);
  private readonly zone = inject(NgZone);
  private readonly http = new HttpClient(inject(HttpBackend));

  readonly refreshUrl = `${this.config.baseApi}${REFRESH_PATH}`;

  /** Shared result of the refresh currently in flight, if any. */
  private inFlight: Observable<string> | null = null;

  isRefreshUrl(url: string): boolean {
    return url.toLowerCase().includes(REFRESH_PATH);
  }

  currentAccessToken(): string {
    return this.storage.get('token');
  }

  /** Drops any in-flight result so nothing replays after a forced logout. */
  reset(): void {
    this.inFlight = null;
  }

  /**
   * Single-flight refresh: every concurrent caller shares one HTTP call and one
   * result. `ReplaySubject(1)` so a caller that subscribes after completion
   * still receives the token, and so errors propagate (a `BehaviorSubject`
   * would retain a stale success value past a later failure).
   */
  refresh(): Observable<string> {
    if (this.inFlight) return this.inFlight;

    const subject = new ReplaySubject<string>(1);
    const shared = subject.asObservable();
    this.inFlight = shared;

    this.runRefresh().then(
      (token) =>
        // `navigator.locks` is not zone-patched, so this settles outside the
        // Angular zone. Without re-entering, the replayed request's response
        // lands outside the zone and the UI silently never re-renders.
        this.zone.run(() => {
          // Cleared before emitting: subscribers run synchronously, and a
          // replay that 401s again must be able to start a *new* refresh
          // rather than re-subscribe to this completed subject and hang.
          this.inFlight = null;
          // A rotated token may carry different claims.
          this.permissions.loadPermissionsFromToken();
          subject.next(token);
          subject.complete();
        }),
      (error: unknown) =>
        this.zone.run(() => {
          this.inFlight = null;
          subject.error(
            error instanceof Error
              ? error
              : new SessionRefreshError('Token refresh failed', error),
          );
        }),
    );

    // Returns the captured observable, not `this.inFlight` — if runRefresh()
    // settles synchronously (a mocked backend in a test) that field is already
    // null by the time we get here.
    return shared;
  }

  private async runRefresh(): Promise<string> {
    const tokenAtEntry = this.storage.get('token');

    return this.withCrossTabLock(async () => {
      // Another tab may have rotated the token while we waited on the lock.
      const current = this.storage.get('token');
      if (current && current !== tokenAtEntry && !isExpired(current)) {
        return current;
      }

      const refreshToken = this.storage.get('refreshToken');
      if (!refreshToken) {
        throw new SessionRefreshError('No refresh token available');
      }

      const body: RefreshTokenPayload = {
        token: current || tokenAtEntry,
        refreshToken,
      };

      let response: RefreshTokenResponse | null = null;
      let httpError: unknown = null;

      try {
        response = await firstValueFrom(
          this.http.post<RefreshTokenResponse>(this.refreshUrl, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Accept: 'application/json',
            }),
          }),
        );
      } catch (error) {
        httpError = error;
        // A failure may arrive as an HTTP error status whose body is the same
        // envelope, so keep reading it rather than bailing here.
        response = (error as HttpErrorResponse)?.error ?? null;
      }

      const pair = extractTokenPair(response);
      const exp = pair ? readExpMs(pair.token) : null;

      if (!pair || exp === null || exp <= Date.now()) {
        // Last resort for browsers without Web Locks: did another tab succeed
        // while this one was failing? If so the failure is benign.
        const latest = this.storage.get('token');
        if (latest && latest !== tokenAtEntry && !isExpired(latest)) {
          return latest;
        }
        throw new SessionRefreshError(
          response?.message || 'Invalid or expired refresh token',
          httpError,
        );
      }

      this.storage.set('token', pair.token);
      if (pair.refreshToken) this.storage.set('refreshToken', pair.refreshToken);
      if (pair.tokenType) this.storage.set('tokenType', pair.tokenType);

      return pair.token;
    });
  }

  /**
   * Serialises refreshes across tabs. localStorage is shared, so without this
   * two tabs fire two POSTs with the same refresh token; if the backend rotates
   * and invalidates, the loser force-logs-out and wipes the storage of the tab
   * that just succeeded. Falls back to running unlocked where Web Locks is
   * unavailable — the post-hoc reconciliation above covers that case.
   */
  private withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
    if (!locks?.request) return fn();
    return locks.request(CROSS_TAB_LOCK, fn);
  }
}
